import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, TextField, Typography } from "@mui/material";
import ActionButton from "src/components/shared/ActionButton";
import SaveError from "src/components/shared/SaveError";
import { casesApi } from "./api";

const AT_ONCE = 4;
const MAX = 4000;
const cases = (n) => (n === 1 ? "1 case" : `${n} cases`);

// One team note on many cases at once (CMS review round 2, rania-11): "Sami
// is off sick, Hala has these" on every case handed over. Notes are for the
// team: nobody is emailed. Four at a time, then one summary; a failure says
// how many didn't get it, and nothing is undone.
export default function BulkNote({ cases: picked, onClose, onDone }) {
  const dispatch = useDispatch();
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null); // { done, failed }
  const running = progress && progress.done < picked.length;

  const run = async () => {
    const text = body.trim();
    if (!text) {
      setError("Write the note first.");
      return;
    }
    setError("");
    let done = 0;
    let failed = 0;
    setProgress({ done, failed });
    const queue = [...picked];
    const worker = async () => {
      while (queue.length) {
        const item = queue.shift();
        try {
          // eslint-disable-next-line no-await-in-loop
          await casesApi.note(item.id, text, dispatch);
        } catch {
          failed += 1;
        }
        done += 1;
        setProgress({ done, failed });
      }
    };
    await Promise.all(Array.from({ length: Math.min(AT_ONCE, picked.length) }, worker));
    if (failed === picked.length) {
      setProgress(null);
      setError(`The note wasn’t added to any of the ${cases(picked.length)}. Nothing changed. Try again.`);
      return;
    }
    if (failed) toast.warn(`Note added to ${picked.length - failed} of ${cases(picked.length)}. ${failed} didn’t get it; add it to them again.`);
    else toast.success(`Note added to ${cases(picked.length)}. Nobody was emailed.`);
    onDone();
  };

  return (
    <Dialog open onClose={running ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="bulk-note-title">
      <DialogTitle id="bulk-note-title">Add a team note to {cases(picked.length)}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }} id="bulk-note-help">
          The same note goes on each case, under Notes, with your name. Only the team sees notes: nobody is emailed.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="Team note"
          value={body}
          disabled={!!progress}
          onChange={(e) => {
            setBody(e.target.value.slice(0, MAX));
            setError("");
          }}
          inputProps={{ "aria-describedby": "bulk-note-help", maxLength: MAX }}
        />
        {progress && (
          <Box mt={2} role="status" aria-live="polite">
            <LinearProgress variant="determinate" value={(progress.done / picked.length) * 100} sx={{ height: 6, borderRadius: 3 }} />
            <Typography variant="body2" color="text.secondary" mt={0.75}>
              {running ? `Adding to ${progress.done} of ${picked.length}…` : `Done: added to ${picked.length - progress.failed}`}
              {progress.failed ? `, ${progress.failed} failed` : ""}.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <SaveError message={error} sx={{ mx: 3, mb: 1 }} />
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <ActionButton variant="text" size="medium" onClick={onClose} disabled={!!running}>
          Cancel
        </ActionButton>
        <ActionButton variant="contained" size="medium" busy={!!running} busyLabel="Adding…" disabled={!!progress} onClick={run}>
          Add the note to {cases(picked.length)}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

BulkNote.propTypes = {
  cases: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};
